import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'
import useAppView from 'Core/Hooks/useAppView'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { useUser } from 'Core/Hooks/useUser'
import { openURLSafely } from 'Utils/PureHelperFuctions'
import { FullScreenContentModal, PaginationView } from 'V2Components'
import {
  CreateApplicationPolicyFlow,
  CreateApplicationRequestFlow,
  DeleteApplicationModal,
  getApplicationSignInURL,
  getApplicationType,
  isReadOnlyApp,
  useApplicationAccess
} from 'features/applications'
import { getIAMResourceDisplayType } from 'features/iamResources'
import {
  AccessCartProvider,
  createRef,
  createRsrcKey,
  getResourceName,
  getResourcePolicyTags,
  getRsrcURL,
  useAccessCartProvider
} from 'features/resources'
import { ApplicationCard, Button, TargetIcon, Typography } from 'procyon-ui'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { Header } from './Components'

import { useSearchQuery } from 'Core/Hooks/useSearchQuery'
import {
  getTagsDefaultSuggestions,
  ResourceQueryFilterMap,
  ResourceTagSuggesstionsMap,
  useTagsRsrcAccess
} from 'features/tags'
import { AddApplication } from '../../../../AddApplication'
import { CheckboxFilter } from './Components/CheckboxFilter'
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getHTMLDataTestID } from 'infra/utils'

// allowed app types

const ALLOWED_APP_TYPES = ['HTTP']
const USER_CART_KEY = 'userApplications'
const ADMIN_CART_KEY = 'adminApplications'

function ApplicationsList() {
  const { rdpurl, isAgentlessMode, user } = useUser()
  const [searchKey, setSearchKey] = useState('')
  const [selectedTag, setSelectedTag] = useState('')
  const [selectedTagsForPolicy, setSelectedTagsForPolicy] = useState([])
  const [showEditAppModal, setShowEditAppModal] = useState(false)
  const [showDeleteAppModal, setShowDeleteAppModal] = useState(false)
  const [showAddApplication, setShowAddApplication] = useState(false)
  const { getObjectRef: getRsrcsRef } = useMultiSlice([
    'serviceAccounts',
    'githubResources',
    'applicationList'
  ])
  const [activeCheckboxFilters, setActiveCheckboxFilters] = useState([])
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [views, setViews] = useState({
    showEntitySlectionModal: false,
    accessRequestSubmissionModal: false
  })

  const { applySearchQuery, getSuggestionsData, getParsedQueryKeyItems } = useSearchQuery({
    queryKey: searchKey,
    defaultQueryFunction: (e, k) => {
      const name = getResourceName(e)
      return name.toLowerCase().includes(k)
    },
    queryMapKeyExtarctor: 'ObjectMeta.Kind',
    queryMap: ResourceQueryFilterMap,
    suggesstionsMap: ResourceTagSuggesstionsMap
  })

  const history = useHistory()
  const { slices, selectDispatch } = useMultiSlice([
    'applicationList',
    'accountList',
    'salesForceAccountList',
    'salesForcePermissionList',
    'salesForceUsersList'
  ])
  const { getApplicationRsrcsAccessMap } = useApplicationAccess()

  const { appView } = useAppView()
  const accessObject = getApplicationRsrcsAccessMap(user)
  const { getRsrcTagPolicies } = useTagsRsrcAccess()

  const { addItemsToCart, isResourceInCart, isCartEmpty, cartItems } = useAccessCartProvider()

  const handleViewsChange = (v) => {
    setViews((s) => ({ ...s, ...v }))
  }

  const getTagsDropdownOptions = () => {
    const options = new Set()

    slices.applicationList.forEach((s) => {
      const tags = getResourcePolicyTags(s)
      if (tags.hasOwnProperty('appgrp')) {
        const { appgrp, ...rest } = tags
        // Split the comma-separated values
        const values = appgrp.split(',')
        // Add unique values to the set
        values.forEach((value) => {
          options.add(value.trim())
        })
      }
    })

    return [...options].map((value) => ({ label: value, value: value }))
  }

  const handleAccessClick = (app) => {
    // if app kind is salesforce then direct redirect to permission tab
    if (app.Spec.AppType === 'salesforce') {
      return history.push(
        `/${appView}/applications/${encodeURIComponent(app.ObjectMeta.Name)}/${encodeURIComponent(
          'permissions'
        )}`
      )
    }

    const existingCartItemsappType = []
    if (!isCartEmpty) {
      cartItems.forEach(({ Resource }) => {
        let rsrc = null
        rsrc = getRsrcsRef(Resource)
        if (!rsrc) return
        const appType = getIAMResourceDisplayType(rsrc)
        existingCartItemsappType.push(appType)
      })

      if (!existingCartItemsappType.some((type) => ['Dropbox', 'github'].includes(type))) {
        if (
          !isResourceInCart(createRef(app)) &&
          app.Spec.AppType === 'http'
          // ||
          // app.Spec.AppType === 'mcp'
        ) {
          return addItemsToCart({
            resourceRef: createRef(app),
            principal: '',
            roles: []
          })
        }
      }
    } else {
      return addItemsToCart({
        resourceRef: createRef(app),
        principal: '',
        roles: []
      })
    }
  }

  const getAppAccessState = (app) => {
    let accessState = 'access'
    const rsrcTagPolicies = getRsrcTagPolicies(app)
    if (accessObject[createRsrcKey(app)] || rsrcTagPolicies.length) accessState = 'signIn'
    //@ts-ignore
    if (isResourceInCart(createRef(app))) accessState = 'requested'

    return accessState
  }

  const getDotMenuItems = ({ app, isAppGranted }) => {
    const items = []
    const existingCartItemsappType = []
    if (isCartEmpty && app.Spec.AppType !== 'http') {
      if (appView === 'admin' && app.Spec.AppType !== 'github') {
        items.push({
          title: 'Create Policy',
          action: () => handleAccessClick(app)
        })
      }

      if (appView === 'user' && !isAppGranted) {
        items.push({
          title: 'Create Request',
          action: () => handleAccessClick(app)
        })
      }
    } else {
      cartItems.forEach(({ Resource }) => {
        let rsrc = null
        rsrc = getRsrcsRef(Resource)
        if (!rsrc) return
        const appType = getIAMResourceDisplayType(rsrc)
        existingCartItemsappType.push(appType)
      })
    }

    if (!existingCartItemsappType.some((type) => ['Dropbox', 'github'].includes(type))) {
      if (!isResourceInCart(createRef(app)) && appView === 'admin' && app.Spec.AppType === 'http') {
        items.push({
          title: 'Create Policy',
          action: () => handleAccessClick(app)
        })
      }
    }

    if (appView == 'admin') {
      items.push(
        {
          title: 'Edit',
          action: () => {
            setSelectedApplication(app)
            setShowEditAppModal(true)
          }
        },
        {
          title: <span className='text-red-600'>Delete</span>,
          action: () => {
            setSelectedApplication(app)
            setShowDeleteAppModal(true)
          }
        }
      )
    }

    const isCreatePolicyExist = items.some(
      (item) => item.title === 'Create Policy' && appView === 'admin'
    )

    if (!isCreatePolicyExist && app.Spec.AppType !== 'mcp') {
      items.push({
        title: 'Sign In',
        action: () => {
          getSingInUrlWithAgentlessCheck(app)
        }
      })
    }

    return items
  }

  const isSearchMatch = (app) => {
    if (searchKey) {
      const data = `${getResourceName(app)}${getApplicationType(app)}`
      if (data.toLowerCase().includes(searchKey)) return true
      else return false
    }
    return true
  }

  // handle github
  const getIsReadOnly = (app) => {
    if (app.Spec.AppType === 'github' && appView === 'user') {
      return true
    } else {
      return isReadOnlyApp(app)
    }
  }

  const getFilteredApps = () => {
    let filteredList = slices.applicationList.filter((app) =>
      ALLOWED_APP_TYPES.includes(app.Spec.AppType.toUpperCase())
    )
    if (activeCheckboxFilters.length !== 0) {
      let matchedFilter = filteredList.filter((app) =>
        activeCheckboxFilters.includes(app.Spec.AppType)
      )
      return matchedFilter.filter(isSearchMatch)
    }

    return applySearchQuery(
      filteredList.filter((app) => {
        // Filter by tag
        if (selectedTag) {
          const { appgrp } = getResourcePolicyTags(app)
          const spliteTags = appgrp?.split(',')
          const tagMatch = spliteTags?.some((tag) => tag.trim() === selectedTag.trim())
          if (tagMatch === '-') return
          if (!tagMatch) return
        }
        return true
      })
    )
  }

  const getSignInStatus = (type) => {
    return type === 'MCP' ? false : true
  }

  const getGrantedState = (accessStatus) => {
    return accessStatus === 'signIn' && appView === 'user' ? true : false
  }

  const renderApplicationCardFunction = (app) => {
    const serviceType = getApplicationType(app)
    const data = {
      name: getResourceName(app),
      accessState: getAppAccessState(app),
      serviceType,
      resourceType: serviceType,
      key: createRsrcKey(app),
      isReadOnly: getIsReadOnly(app),
      dotMenuItems: getDotMenuItems({ app, isAppGranted: getAppAccessState(app) === 'signIn' }),
      onAccessClick: () => handleAccessClick(app),
      app,
      granted: getGrantedState(getAppAccessState(app))
    }

    //@ts-ignore
    return (
      <ApplicationCard
        data-testid={getHTMLDataTestID('apps', 'div', 'card', data.name)}
        onClickSignIn={() => getSingInUrlWithAgentlessCheck(data.app)}
        onClickMoreInfo={() => handleMoreInfoClick(data.app)}
        onClickPolicy={data.onAccessClick}
        onClickRequest={data.onAccessClick}
        forPolicy={appView === 'admin'}
        isReadOnly={data.isReadOnly}
        width='100%'
        key={data.key}
        //@ts-ignore
        accessState={data.accessState}
        dotMenuItems={
          data.dotMenuItems.length
            ? [
                {
                  icon: faEllipsisVertical,
                  submenu: data.dotMenuItems,
                  title: ''
                }
              ]
            : []
        }
        isGranted={data.granted || false}
        resourceName={data.name}
        resourceType={data.resourceType}
        //@ts-ignore
        showSignIn={getSignInStatus(data.resourceType)}
        serviceType={ServiceTypeIconMap[data.serviceType]}
        customIcon={data.serviceType === 'SALESFORCE' && <TargetIcon type='SALESFORCE' />}
      />
    )
  }

  const handleMoreInfoClick = (app) => history.push(getRsrcURL(app, appView))

  useEffect(() => {
    selectDispatch([
      'applicationList',
      'accountList',
      'policyList',
      'userList',
      'groupList',
      'medusaNodes',
      'salesForceAccountList',
      'salesForcePermissionList'
    ])
  }, [])

  const getSingInUrlWithAgentlessCheck = (app) => {
    let url = getApplicationSignInURL(app)

    if (isAgentlessMode) {
      url = url.replace('.default', '-app-default').replace('.procyon.ai', `.${rdpurl}`)
      url && openURLSafely(url)
    } else {
      url && openURLSafely(url)
    }
  }

  const apps = getFilteredApps()
  const tagMap = getParsedQueryKeyItems()
  const tag = tagMap.tag || tagMap.ptag

  const disableAddBtn = () => {
    if (selectedTagsForPolicy.includes(tagMap.tag || tagMap.ptag)) return true
    if (tag?.split('!').filter((e) => e.trim().length).length > 1) return false
    return true
  }

  const showTagPolicyBtns = () => {
    if (appView === 'user') return false
    if (tag?.split('!').filter((e) => e.trim().length).length > 1) return true
    if (selectedTagsForPolicy.length) return true
    else return false
  }

  const handleCreateTagPolicyCreate = () => {
    history.push(
      `/admin/tags/policy/create?${tagMap.tag ? 'tag' : 'ptag'}=${JSON.stringify(
        selectedTagsForPolicy
      )}&scope=${JSON.stringify(['Application'])}`
    )
  }

  return (
    <div className='flex justify-between h-[100vh]'>
      {/* <div className='w-[20%] pl-5'>
        <CheckboxFilter
          activeFilters={activeCheckboxFilters}
          onActiveFilterChange={setActiveCheckboxFilters}
        />
      </div> */}
      <div className='w-[100%] p-5'>
        <Header
          searchKey={searchKey}
          setSearchKey={setSearchKey}
          tags={getTagsDropdownOptions()}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          suggestions={getSuggestionsData(slices.applicationList)}
          defaultSuggestions={getTagsDefaultSuggestions(slices.applicationList)}
          TagComponent={
            showTagPolicyBtns() && (
              <div className='relative z-[1000]'>
                <div className='flex gap-2'>
                  <Button
                    data-testid={getHTMLDataTestID('apps', 'button', 'create-tag-policy')}
                    disabled={selectedTagsForPolicy.length === 0}
                    size='sm'
                    onClick={handleCreateTagPolicyCreate}
                    variant='primary'
                  >
                    Create Tag Policy
                  </Button>
                  <Button
                    data-testid={getHTMLDataTestID('apps', 'button', 'add-tag-policy')}
                    icon={faPlus}
                    disabled={disableAddBtn()}
                    size='sm'
                    onClick={() => {
                      setSelectedTagsForPolicy((s) => [tagMap.tag || tagMap.ptag, ...s])
                    }}
                    variant='grayBlue'
                  >
                    Add
                  </Button>
                </div>
                {selectedTagsForPolicy.length > 0 && (
                  <div className='absolute min-w-[230px] bg-white rounded-sm drop-shadow p-2 w-[auto] flex gap-2 flex-wrap mt-2'>
                    {selectedTagsForPolicy.map((t) => {
                      return (
                        <Typography
                          data-testid={getHTMLDataTestID('apps', 'p', 'tag-name', t)}
                          className='w-[100%] flex justify-between items-center !text-[13px]'
                          key={t}
                          variant='body-regular'
                        >
                          {t}
                          <span
                            onClick={() => {
                              setSelectedTagsForPolicy((s) => s.filter((e) => e !== t))
                            }}
                            className='px-2 ml-2 rounded bg-gray-100 cursor-pointer hover:opacity-[0.5]'
                          >
                            <FontAwesomeIcon color='#f87171' icon={faMinus} />
                          </span>
                        </Typography>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }
        />

        <PaginationView
          itemsCount={40}
          data={apps}
          renderFunction={renderApplicationCardFunction}
          WrapperComponent={({ children }) => (
            <div className='grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 grid gap-8 mt-4'>
              {children}
            </div>
          )}
        />
      </div>

      {appView === 'admin' && (
        <CreateApplicationPolicyFlow views={views} setViews={handleViewsChange} />
      )}
      {appView === 'user' && <CreateApplicationRequestFlow setViews={setViews} views={views} />}
      {showDeleteAppModal && (
        <DeleteApplicationModal
          cleanUpFn={() => {
            setSelectedApplication(null)
            setShowDeleteAppModal(false)
          }}
          app={selectedApplication}
        />
      )}

      {showAddApplication && (
        <FullScreenContentModal>
          <AddApplication
            type=''
            onComplete={() => setShowAddApplication(false)}
            onClose={() => setShowAddApplication(false)}
          />
        </FullScreenContentModal>
      )}

      {showEditAppModal && (
        <FullScreenContentModal>
          <AddApplication
            type=''
            onComplete={() => setShowEditAppModal(false)}
            app={selectedApplication}
            onClose={() => setShowEditAppModal(false)}
          />
        </FullScreenContentModal>
      )}
    </div>
  )
}

const WrappedApplicationsList = () => {
  const { appView } = useAppView()
  const cartKey = appView === 'admin' ? ADMIN_CART_KEY : USER_CART_KEY

  return (
    <AccessCartProvider cartKey={cartKey}>
      <ApplicationsList />
    </AccessCartProvider>
  )
}

const ServiceTypeIconMap = {
  AWS: 'aws',
  AZURE: 'azure',
  GCP: 'gcp',
  HTTP: 'apiRest',
  MCP: 'mcp'
}

export { WrappedApplicationsList as ApplicationsList }
